/**
 * The stylesheet every Dayvise surface needs, generated from the tokens
 * (prompt.md §40, §44).
 *
 * WHY a stylesheet at all when components style themselves inline: inline styles
 * cannot express three things this product genuinely needs —
 *
 *  1. interactive states (`:hover`, `:active`, `:focus-visible`), which are how
 *     a keyboard user sees where they are;
 *  2. keyframes (the run spinner and the loading shimmer);
 *  3. `prefers-reduced-motion` and `prefers-color-scheme`.
 *
 * So the split is deliberate: layout and colour **variants** are computed in
 * TypeScript and applied inline (so nothing is duplicated per component), while
 * states, schemes and animation live in one generated block whose values still
 * come from `@dayvise/design-tokens`. No CSS framework is involved, and no
 * component contains a colour literal.
 */

import { cssVariables } from "@dayvise/design-tokens";

/**
 * Class names components rely on. Exported so a component never writes a
 * literal class string the stylesheet might not know about.
 */
export const CLASS_NAMES = {
  /** Adds hover/active transitions driven by `--dayvise-control-hover`. */
  control: "dayvise-control",
  /** Adds the accent focus-visible ring. */
  focusable: "dayvise-focusable",
  /** A text input or textarea that highlights its border while focused. */
  field: "dayvise-field",
  /** Marks an element whose animation must stop under reduced motion. */
  animated: "dayvise-animated",
} as const;

/** CSS custom properties a control sets inline to describe its own states. */
export const STATE_VARIABLES = {
  hoverBackground: "--dayvise-control-hover",
  activeBackground: "--dayvise-control-active",
} as const;

/**
 * Build the complete stylesheet.
 *
 * The **jetblack dark scheme is the default on `:root`**, so a client that has
 * never chosen a scheme (or has not hydrated yet) renders the reference look.
 * The white/light scheme applies explicitly through a
 * `data-dayvise-scheme="light"` attribute anywhere up the tree. Dark is not
 * inferred from the operating system: the product ships dark, and the user
 * toggles to light.
 */
export function dayviseStylesheet(): string {
  const darkOnRoot = cssVariables("dark", ":root");
  const darkOnAttribute = cssVariables("dark", '[data-dayvise-scheme="dark"]');
  const lightOnAttribute = cssVariables("light", '[data-dayvise-scheme="light"]');
  return [darkOnRoot, darkOnAttribute, lightOnAttribute, SCHEME_RULES, INTERACTIVE_RULES, KEYFRAMES].join("\n\n");
}

/** Native form-control and scrollbar colours follow the active scheme. */
const SCHEME_RULES = [
  ":root { color-scheme: dark; }",
  '[data-dayvise-scheme="light"] { color-scheme: light; }',
].join("\n");

const INTERACTIVE_RULES = [
  `.${CLASS_NAMES.control} { transition: background var(--dayvise-motion-duration-fast) var(--dayvise-motion-easing-standard), color var(--dayvise-motion-duration-fast) var(--dayvise-motion-easing-standard); }`,
  `.${CLASS_NAMES.control}:not(:disabled):hover { background: var(--dayvise-control-hover, transparent); }`,
  `.${CLASS_NAMES.control}:not(:disabled):active { background: var(--dayvise-control-active, var(--dayvise-control-hover, transparent)); }`,
  `.${CLASS_NAMES.focusable}:focus-visible { outline: 2px solid var(--dayvise-color-accent); outline-offset: 2px; }`,
  `.${CLASS_NAMES.field}:focus { border-color: var(--dayvise-color-accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--dayvise-color-accent) 35%, transparent); }`,
  `@media (prefers-reduced-motion: reduce) { .${CLASS_NAMES.animated} { animation: none !important; transition: none !important; } }`,
].join("\n");

const KEYFRAMES = [
  "@keyframes dayvise-spin { to { transform: rotate(360deg); } }",
  "@keyframes dayvise-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }",
  "@keyframes dayvise-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }",
].join("\n");
